import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useModuleData } from "@/hooks/useModuleData";
import { usePagedData } from "@/hooks/usePagedData";
import { useSite } from "@/context/SiteContext";
import { PageHeader } from "@/components/common/PageHeader";
import { DataTable } from "@/components/common/DataTable";
import { KpiCard } from "@/components/common/KpiCard";
import { Loader } from "@/components/common/Loader";
import { Pager, SearchInput } from "@/components/common/Pager";
import { MrpPanel } from "@/pages/MrpPanel";
import { CapacityPanel } from "@/pages/CapacityPanel";
import { num, shortDate } from "@/lib/format";
import api from "@/lib/api";
import { Factory, Plus, Play, CheckCircle2, ClipboardCheck, FileText } from "lucide-react";
import { openPdf } from "@/lib/pdf";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";

const ACCENT = "#3B82F6";
const tt = "data-[state=active]:bg-[#e6effd] data-[state=active]:text-[#3B82F6]";

export default function Production() {
  const navigate = useNavigate();
  const { data: orders, loading, reload } = useModuleData("/production-orders");
  const { data: boms } = useModuleData("/boms");
  const { data: wcs } = useModuleData("/work-centers");
  const routings = usePagedData("/routings");
  const { sites, activeSite } = useSite();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ material_code: "", target_qty: "", work_center: "", due_date: "", site_id: "" });
  const [confirmPo, setConfirmPo] = useState(null);
  const [confForm, setConfForm] = useState({ yield_qty: "", scrap_qty: "", allow_over: false });

  if (loading || !orders) return <Loader />;

  const active = orders.filter((o) => ["RELEASED", "IN_PROGRESS"].includes(o.status)).length;
  const totalTarget = orders.reduce((s, o) => s + o.target_qty, 0);
  const totalProduced = orders.reduce((s, o) => s + o.produced_qty, 0);
  const avgScrap = orders.length ? (orders.reduce((s, o) => s + o.scrap_pct, 0) / orders.length).toFixed(1) : 0;

  const advance = async (o) => {
    const next = { CREATED: "RELEASED", RELEASED: "IN_PROGRESS", IN_PROGRESS: "CONFIRMED", CONFIRMED: "TECO" }[o.status];
    if (!next) return;
    await api.patch(`/production-orders/${o.id}/status?status=${next}`);
    toast.success(`Order ${o.order_no} → ${next.replace(/_/g, " ")}`);
    reload();
  };

  const create = async () => {
    const bom = boms?.find((b) => b.material_code === form.material_code);
    try {
      await api.post("/production-orders", {
        site_id: form.site_id || (activeSite !== "GROUP" ? activeSite : sites[0]?.id),
        material_code: form.material_code, material_name: bom?.name || form.material_code,
        target_qty: Number(form.target_qty), work_center: form.work_center, due_date: form.due_date,
      });
      toast.success("Production order created");
      setOpen(false); setForm({ material_code: "", target_qty: "", work_center: "", due_date: "", site_id: "" });
      reload();
    } catch (e) { toast.error("Failed to create order"); }
  };

  const submitConfirm = async () => {
    if (!confForm.yield_qty) { toast.error("Enter a yield quantity"); return; }
    try {
      const { data } = await api.post(`/production-orders/${confirmPo.id}/confirm`, { yield_qty: Number(confForm.yield_qty), scrap_qty: Number(confForm.scrap_qty || 0), allow_over: !!confForm.allow_over });
      toast.success(`Confirmed ${confForm.yield_qty}${data.over_delivery ? " (over-delivery)" : ""} · ${data.status} · weighted scrap ${data.weighted_scrap_pct}% · ${data.components_flushed.length} components back-flushed`);
      setConfirmPo(null); setConfForm({ yield_qty: "", scrap_qty: "", allow_over: false }); reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Confirmation failed"); }
  };

  const columns = [
    { key: "order_no", label: "Order", render: (r) => <span className="font-mono text-xs text-[#3B82F6]">{r.order_no}</span> },
    { key: "material_name", label: "Product", render: (r) => (
      <div><div className="text-[#32363a]">{r.material_name}</div><div className="font-mono text-[10px] text-[#6a6d70]">{r.material_code}</div></div>) },
    { key: "target_qty", label: "Target", render: (r) => <span className="font-mono">{num(r.target_qty)}</span> },
    { key: "progress", label: "Progress", render: (r) => {
      const pct = Math.min(100, Math.round((r.produced_qty / r.target_qty) * 100) || 0);
      return (<div className="w-28"><div className="flex justify-between text-[10px] text-[#6a6d70]"><span>{num(r.produced_qty)}</span><span>{pct}%</span></div>
        <div className="mt-1 h-1.5 rounded bg-[#eef1f5]"><div className="h-1.5 rounded bg-[#3B82F6]" style={{ width: `${pct}%` }} /></div></div>); } },
    { key: "work_center", label: "Work Center", render: (r) => <span className="font-mono text-xs text-[#6a6d70]">{r.work_center}</span> },
    { key: "scrap_pct", label: "Scrap %", render: (r) => <span className="font-mono">{r.scrap_pct}%</span> },
    { key: "status", label: "Status", badge: true },
    { key: "actions", label: "", render: (r) => (
      <div className="flex gap-1.5">
        <button data-testid={`pdf-order-${r.order_no}`} onClick={() => openPdf(`/production-orders/${r.id}/pdf`)}
          className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1 text-[11px] text-[#32363a] hover:border-[#D0313A]"><FileText size={13} /> PDF</button>
        {r.status !== "TECO" && (<>
          <button data-testid={`advance-order-${r.order_no}`} onClick={() => advance(r)}
            className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-[#eaedf2] px-2 py-1 text-[11px] text-[#32363a] hover:border-[#3B82F6]">
            {r.status === "CONFIRMED" ? <CheckCircle2 size={13} /> : <Play size={13} />} Advance</button>
          <button data-testid={`confirm-order-${r.order_no}`} onClick={() => { setConfirmPo(r); setConfForm({ yield_qty: "", scrap_qty: "", allow_over: false }); }}
            className="inline-flex items-center gap-1 rounded-md border border-[#c8cfda] bg-white px-2 py-1 text-[11px] text-[#32363a] hover:border-[#10B981]"><ClipboardCheck size={13} /> Confirm</button>
        </>)}
      </div>) },
  ];

  const bomCols = [
    { key: "material_code", label: "Product", render: (r) => <span className="font-mono text-xs text-[#3B82F6]">{r.material_code}</span> },
    { key: "name", label: "Description" },
    { key: "base_qty", label: "Base Qty", render: (r) => <span className="font-mono">{num(r.base_qty || 1000)}</span> },
    { key: "components", label: "Components", render: (r) => <span className="font-mono">{(r.components || []).length}</span> },
  ];
  const rtgCols = [
    { key: "material_code", label: "Product", render: (r) => <span className="font-mono text-xs text-[#3B82F6]">{r.material_code}</span> },
    { key: "name", label: "Description" },
    { key: "group_no", label: "Group", render: (r) => <span className="font-mono text-[10px] text-[#6a6d70]">{r.group_no}</span> },
    { key: "operations", label: "Operations", render: (r) => <span className="font-mono">{(r.operations || []).length}</span> },
  ];

  return (
    <div>
      <PageHeader title="Production Planning" subtitle="Manufacturing orders, BOMs (EP-PP02), routings (EP-PP03), MRP (EP-PP04) and work-centre capacity (EP-PP05) for the packaging lines." icon={Factory} accent={ACCENT}>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <button data-testid="create-prod-order-btn" className="inline-flex items-center gap-2 rounded-lg bg-[#3B82F6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2563eb]"><Plus size={16} /> New Order</button>
          </DialogTrigger>
          <DialogContent className="border-[#e5e9f0] bg-[#ffffff] text-[#32363a]">
            <DialogHeader><DialogTitle className="uppercase">Create Production Order</DialogTitle></DialogHeader>
            <div className="grid gap-3">
              <select data-testid="prod-material-select" value={form.material_code} onChange={(e) => setForm({ ...form, material_code: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                <option value="">Select product…</option>{(boms || []).map((b) => <option key={b.material_code} value={b.material_code}>{b.name}</option>)}
              </select>
              {activeSite === "GROUP" && (
                <select data-testid="prod-site-select" value={form.site_id} onChange={(e) => setForm({ ...form, site_id: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                  <option value="">Select site…</option>{sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>)}
              <input data-testid="prod-qty-input" type="number" placeholder="Target quantity" value={form.target_qty} onChange={(e) => setForm({ ...form, target_qty: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
              <select data-testid="prod-wc-select" value={form.work_center} onChange={(e) => setForm({ ...form, work_center: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm">
                <option value="">Select work center…</option>{[...new Set((wcs || []).map((w) => w.wc_code))].map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="prod-due-input" type="date" value={form.due_date} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
            </div>
            <DialogFooter>
              <button data-testid="prod-create-submit" onClick={create} disabled={!form.material_code || !form.target_qty || !form.work_center} className="rounded-lg bg-[#3B82F6] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Create Order</button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </PageHeader>

      <div className="mb-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Active Orders" value={active} accent={ACCENT} />
        <KpiCard label="Target Output" value={num(totalTarget)} sub="pcs planned" accent={ACCENT} />
        <KpiCard label="Produced" value={num(totalProduced)} sub="pcs confirmed" accent="#10B981" />
        <KpiCard label="Avg Scrap" value={`${avgScrap}%`} accent="#F97316" />
      </div>

      <Tabs defaultValue="orders">
        <TabsList className="bg-white border border-[#e5e9f0] flex-wrap h-auto">
          <TabsTrigger data-testid="tab-orders" value="orders" className={tt}>Orders (EP-PP01)</TabsTrigger>
          <TabsTrigger data-testid="tab-bom" value="bom" className={tt}>BOM (EP-PP02)</TabsTrigger>
          <TabsTrigger data-testid="tab-routing" value="routing" className={tt}>Routings (EP-PP03)</TabsTrigger>
          <TabsTrigger data-testid="tab-mrp" value="mrp" className={tt}>MRP (EP-PP04)</TabsTrigger>
          <TabsTrigger data-testid="tab-capacity" value="capacity" className={tt}>Work Centres (EP-PP05)</TabsTrigger>
        </TabsList>

        <TabsContent value="orders" className="mt-4"><DataTable testId="prod-orders-table" columns={columns} rows={orders} /></TabsContent>

        <TabsContent value="bom" className="mt-4">
          <div className="mb-3 flex justify-end"><button data-testid="new-bom-btn" onClick={() => navigate("/bom/new")} className="inline-flex items-center gap-2 rounded-lg bg-[#3B82F6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2563eb]"><Plus size={16} /> New BOM (EP-PP02)</button></div>
          <DataTable testId="bom-table" columns={bomCols} rows={boms || []} onRowClick={(r) => navigate(`/bom/${encodeURIComponent(r.material_code)}`)} />
        </TabsContent>

        <TabsContent value="routing" className="mt-4">
          <div className="mb-3 flex items-center justify-between gap-2">
            <SearchInput testId="routing-search" value={routings.q} onChange={routings.setQ} placeholder="Search routings by product or description…" />
            <button data-testid="new-routing-btn" onClick={() => navigate("/routing/new")} className="inline-flex items-center gap-2 rounded-lg bg-[#3B82F6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2563eb]"><Plus size={16} /> New Routing (EP-PP03)</button>
          </div>
          {routings.loading && routings.items.length === 0 ? <Loader /> : <DataTable testId="routing-table" columns={rtgCols} rows={routings.items} onRowClick={(r) => navigate(`/routing/${encodeURIComponent(r.material_code)}`)} />}
          <Pager testId="routing-pager" page={routings.page} pages={routings.pages} total={routings.total} pageSize={routings.pageSize} onPage={routings.setPage} />
        </TabsContent>

        <TabsContent value="mrp" className="mt-4"><MrpPanel /></TabsContent>
        <TabsContent value="capacity" className="mt-4"><CapacityPanel /></TabsContent>
      </Tabs>

      <Dialog open={!!confirmPo} onOpenChange={(o) => !o && setConfirmPo(null)}>
        <DialogContent className="border-[#e5e9f0] bg-white text-[#32363a]">
          <DialogHeader><DialogTitle className="uppercase">Confirm Order · EP-PP06 · S/4 COR6 {confirmPo ? `· ${confirmPo.order_no}` : ""}</DialogTitle></DialogHeader>
          {confirmPo && (() => {
            const remaining = Math.max(0, +(confirmPo.target_qty - confirmPo.produced_qty).toFixed(2));
            const over = Number(confForm.yield_qty) > remaining;
            const hist = confirmPo.confirmations || [];
            return (
            <div className="grid gap-3">
              <div className="flex flex-wrap items-center justify-between gap-1 text-sm">
                <span className="text-[#6a6d70]">{confirmPo.material_name}</span>
                <span className="font-mono text-[#32363a]">target {num(confirmPo.target_qty)} · produced {num(confirmPo.produced_qty)} · <span className="font-semibold text-[#3B82F6]">remaining {num(remaining)}</span></span>
              </div>
              <input data-testid="confirm-yield-input" type="number" placeholder={`Yield quantity (GR) · max ${num(remaining)}`} value={confForm.yield_qty} onChange={(e) => setConfForm({ ...confForm, yield_qty: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
              <input data-testid="confirm-scrap-input" type="number" placeholder="Scrap quantity (optional)" value={confForm.scrap_qty} onChange={(e) => setConfForm({ ...confForm, scrap_qty: e.target.value })} className="rounded-lg border border-[#e5e9f0] bg-[#eef1f5] px-3 py-2 text-sm" />
              {over && (
                <label data-testid="confirm-allow-over" className="flex items-center gap-2 rounded-lg border border-[#f0b100] bg-[#fff8e6] px-3 py-2 text-xs text-[#8a6d00]">
                  <input type="checkbox" checked={!!confForm.allow_over} onChange={(e) => setConfForm({ ...confForm, allow_over: e.target.checked })} />
                  Yield exceeds remaining {num(remaining)} — allow over-delivery
                </label>)}
              {hist.length > 0 && (
                <div className="rounded-lg border border-[#e5e9f0]">
                  <div className="border-b border-[#e5e9f0] px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-[#6a6d70]">Confirmation History · weighted scrap {confirmPo.scrap_pct}%</div>
                  <div className="max-h-40 divide-y divide-[#f0f2f5] overflow-auto">
                    {hist.map((h) => (
                      <div key={h.conf_no} data-testid={`conf-hist-${h.conf_no}`} className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs">
                        <span className="font-mono text-[#6a6d70]">#{h.conf_no} · {shortDate(h.timestamp)}</span>
                        <span className="text-[#32363a]">yield {num(h.yield_qty)} · scrap {num(h.scrap_qty)} ({h.scrap_pct}%)</span>
                        <span className="text-[#89919a]">{h.operator}{h.over_delivery ? " ⚠" : ""}</span>
                      </div>))}
                  </div>
                </div>)}
              <p className="text-xs text-[#6a6d70]">Posts a 101 GR for the finished goods and back-flushes BOM components as 261 goods issues.</p>
            </div>); })()}
          <DialogFooter><button data-testid="confirm-submit-btn" onClick={submitConfirm} disabled={!confForm.yield_qty} className="rounded-lg bg-[#10B981] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">Confirm &amp; Post</button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
